import { useEffect, useRef } from "react";
import { supabase } from "./supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";

// Oct 28, 2026: two hooks on the same table that mount in the same millisecond (the Builds board and its
// Trades view both watch trade_items) got the same Date.now() name; supabase.channel() handed the second
// one the first's already-subscribed channel and .on() threw, white-screening the page. A counter keeps
// every name unique.
let channelSeq = 0;
const channelSuffix = () => `${Date.now()}-${++channelSeq}`;

/**
 * Subscribe to Supabase real-time changes on a table.
 * Calls `onUpdate` whenever an INSERT, UPDATE, or DELETE event fires.
 *
 * Usage:
 *   useRealtimeTable("deals", reload);          // all events on deals
 *   useRealtimeTable("deals", reload, "UPDATE"); // only updates
 *   useRealtimeTable("dispatch_schedule", reload);
 *
 * The hook manages subscribe/unsubscribe lifecycle automatically.
 */
export function useRealtimeTable(
  table: string,
  onUpdate: () => void,
  eventFilter?: "INSERT" | "UPDATE" | "DELETE",
) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  useEffect(() => {
    const channelName = `realtime-${table}-${eventFilter ?? "all"}-${channelSuffix()}`;
    const channel = supabase.channel(channelName);

    if (eventFilter) {
      channel.on(
        "postgres_changes" as never,
        { event: eventFilter, schema: "public", table },
        () => { callbackRef.current(); }
      );
    } else {
      channel.on(
        "postgres_changes" as never,
        { event: "*", schema: "public", table },
        () => { callbackRef.current(); }
      );
    }

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [table, eventFilter]);
}

/**
 * Subscribe to one deal's rows across several tables (filtered server-side on
 * `deal_id`), debounced so a burst of events — an inbound text's INSERT
 * followed by its status UPDATEs — refetches once. Only tables in the
 * `supabase_realtime` publication fire (twilio_messages, activity_log, deals;
 * twilio_calls is NOT published). A null dealId subscribes to nothing.
 *
 * Usage:
 *   useRealtimeDealChanges(dealId, ["twilio_messages", "activity_log"], reload);
 */
export function useRealtimeDealChanges(
  dealId: string | null | undefined,
  tables: readonly string[],
  onChange: () => void,
  debounceMs = 800,
) {
  const callbackRef = useRef(onChange);
  callbackRef.current = onChange;
  const tableKey = tables.join(",");

  useEffect(() => {
    if (!dealId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        callbackRef.current();
      }, debounceMs);
    };
    const channel = supabase.channel(`realtime-deal-${dealId}-${tableKey.replace(/,/g, "+")}-${channelSuffix()}`);
    for (const table of tableKey.split(",")) {
      channel.on(
        "postgres_changes" as never,
        { event: "*", schema: "public", table, filter: `deal_id=eq.${dealId}` },
        fire,
      );
    }
    channel.subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [dealId, tableKey, debounceMs]);
}

/**
 * Subscribe to changes on multiple tables at once.
 * Useful for views that combine data from several sources.
 *
 * Usage:
 *   useRealtimeTables(["deals", "dispatch_schedule"], reload);
 */
export function useRealtimeTables(
  tables: string[],
  onUpdate: () => void,
) {
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  useEffect(() => {
    const channelName = `realtime-multi-${tables.join("-")}-${channelSuffix()}`;
    const channel = supabase.channel(channelName);

    tables.forEach((table) => {
      channel.on(
        "postgres_changes" as never,
        { event: "*", schema: "public", table },
        () => { callbackRef.current(); }
      );
    });

    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tables.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
}
